/**
 * The passphrase half of the export panel (TanStack Form).
 *
 * Two fields, not one, and the confirmation is not ceremony: this passphrase
 * is the ONLY thing that can ever open the file again. There is no recovery,
 * no hint, and nothing in the file that could verify a typo — a mistyped
 * passphrase produces a backup that is silently useless, and the user finds
 * out on the day they need it. So the form says that, in words, next to the
 * fields.
 *
 * The values live in form state and nowhere else: never in a zustand store,
 * never in `localStorage`, and never in an analytics property.
 */
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { MIN_TRANSFER_PASSPHRASE_LENGTH } from '../../../types/connectionTransfer';
import type { ExportPassphraseFormApi } from './exportPassphraseForm';

/**
 * `children` is where the caller drops the "the two entries don't match yet"
 * hint. It is rendered by the panel rather than here because `form.Subscribe`
 * only keeps its generics on the concretely-inferred form object, not on the
 * `FormApi` alias a prop has to use.
 */
export function ExportPassphraseFields({
  form,
  children,
}: {
  form: ExportPassphraseFormApi;
  children?: React.ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-md border border-border p-3" data-testid="export-passphrase">
      <p className="text-xs text-muted-foreground">
        Saved passwords and SSH secrets are encrypted with this passphrase before they are
        written. Jam SQL Studio cannot recover it and the file carries no hint — if you lose it,
        the passwords in this backup are gone. Store it somewhere other than next to the file.
      </p>

      <form.Field
        name="passphrase"
        validators={{
          onChange: ({ value }: { value: string }) =>
            value.length > 0 && value.length < MIN_TRANSFER_PASSPHRASE_LENGTH
              ? `Use at least ${MIN_TRANSFER_PASSPHRASE_LENGTH} characters.`
              : undefined,
        }}
      >
        {(field) => (
          <div className="space-y-1">
            <Label htmlFor="connections-export-passphrase" className="text-xs">
              Passphrase
            </Label>
            <Input
              id="connections-export-passphrase"
              data-testid="connections-export-passphrase"
              type="password"
              autoComplete="new-password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
            {field.state.meta.errors.length > 0 ? (
              <p className="text-xs text-destructive">{String(field.state.meta.errors[0])}</p>
            ) : null}
          </div>
        )}
      </form.Field>

      <form.Field
        name="confirm"
        validators={{
          // Read through the form rather than a captured value: a nested
          // Subscribe would re-render the field on every keystroke of the
          // first box and lose its focus/selection.
          onChange: ({ value }: { value: string }) =>
            value.length > 0 && value !== form.getFieldValue('passphrase')
              ? 'The two entries differ.'
              : undefined,
        }}
      >
        {(field) => (
          <div className="space-y-1">
            <Label htmlFor="connections-export-confirm" className="text-xs">
              Confirm passphrase
            </Label>
            <Input
              id="connections-export-confirm"
              data-testid="connections-export-confirm"
              type="password"
              autoComplete="new-password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
            {field.state.meta.errors.length > 0 ? (
              <p className="text-xs text-destructive">{String(field.state.meta.errors[0])}</p>
            ) : null}
          </div>
        )}
      </form.Field>
      {children}
    </div>
  );
}
